import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { VscAdd, VscChevronLeft, VscChevronRight, VscLayers } from 'react-icons/vsc';
import EventForm from './EventForm';
import {
  getProjectEvents, getMyEvents, getBirthdays, getCalendarPrefs, saveCalendarPrefs,
  createEvent, updateEvent, deleteEvent,
} from '../../api/calendar';
import {
  DOW, MONTHS, DAY_NAMES, COLORS, startOfWeek, startOfDay, endOfDay, addDays, sameDay, isoWeek,
  fmtTime, onDay, danishHolidays, birthdays, generatedInRange, layoutColumns,
} from './calendarUtils';
import './Calendar.css';

const HOUR_HEIGHT = 48;
const WRITE_ROLES = ['admin', 'engineer', 'manager', 'foreman'];
const VIEW_KEY    = 'kspro_cal_view';
const PERSONAL_COLOR = '#8e44ad';
const HOLIDAY_COLOR  = '#c0392b';
const BIRTHDAY_COLOR = '#d68910';

// Project names usually already contain the NB no. ("#484 Vigilant") — only add it when missing
const projectLabel = p => {
  if (!p) return 'Project';
  const nb = p.newbuildingNo ? String(p.newbuildingNo) : '';
  return nb && !String(p.name).includes(nb) ? `#${nb} ${p.name}` : p.name;
};

function visibleRange(view, cursor) {
  if (view === 'month') {
    const start = startOfWeek(new Date(cursor.getFullYear(), cursor.getMonth(), 1));
    return { start, end: endOfDay(addDays(start, 41)) };
  }
  if (view === 'week') {
    const start = startOfWeek(cursor);
    return { start, end: endOfDay(addDays(start, 6)) };
  }
  return { start: startOfDay(cursor), end: endOfDay(cursor) };
}

// ── Month view ───────────────────────────────────────────────────────────────
function MonthView({ cursor, events, onDayClick, onEventClick, onShowDay }) {
  const start = startOfWeek(new Date(cursor.getFullYear(), cursor.getMonth(), 1));
  const today = new Date();
  const weeks = Array.from({ length: 6 }, (_, w) => Array.from({ length: 7 }, (_, d) => addDays(start, w * 7 + d)));
  const MAX = 3;

  return (
    <div className="cal-month">
      <div className="cal-month-header">
        <div className="cal-wk-header-label">Wk</div>
        {DOW.map(d => <div key={d} className="cal-month-dow">{d}</div>)}
      </div>
      <div className="cal-month-grid">
        {weeks.map((days, wi) => (
          <React.Fragment key={wi}>
            <div className="cal-wk-num">{isoWeek(days[0])}</div>
            {days.map((day, di) => {
              const dayEvs = events.filter(ev => onDay(ev, day))
                .sort((a, b) => (b.allDay - a.allDay) || (new Date(a.start) - new Date(b.start)));
              return (
                <div key={di}
                  className={`cal-month-cell${day.getMonth() !== cursor.getMonth() ? ' other-month' : ''}${sameDay(day, today) ? ' today' : ''}${di >= 5 ? ' weekend' : ''}`}
                  onClick={() => onDayClick(day)}
                >
                  <div className="cal-day-num">{day.getDate()}</div>
                  {dayEvs.slice(0, MAX).map(ev => (
                    <div key={ev.key} className="cal-month-event" style={{ background: ev.displayColor }}
                      onClick={e => { e.stopPropagation(); onEventClick(ev); }} title={ev.label}>
                      {!ev.allDay && <span className="cal-month-event-time">{fmtTime(new Date(ev.start))} </span>}
                      {ev.label}
                    </div>
                  ))}
                  {dayEvs.length > MAX && (
                    <div className="cal-more-link" onClick={e => { e.stopPropagation(); onShowDay(day); }}>
                      +{dayEvs.length - MAX} more
                    </div>
                  )}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

// ── Week / day view (time grid) ──────────────────────────────────────────────
function TimeGrid({ days, events, onSlotClick, onEventClick, onShowDay }) {
  const bodyRef = useRef(null);
  const today = new Date();
  const cols = `48px repeat(${days.length}, minmax(0, 1fr))`;

  // Start the day at 07:00
  useEffect(() => { if (bodyRef.current) bodyRef.current.scrollTop = 7 * HOUR_HEIGHT; }, []);

  const allDay = events.filter(ev => ev.allDay);
  const timed  = events.filter(ev => !ev.allDay);
  const now = new Date(), nowTop = (now.getHours() + now.getMinutes() / 60) * HOUR_HEIGHT;

  const slotFromClick = (day, e) => {
    const relY = e.clientY - e.currentTarget.getBoundingClientRect().top;
    const slot = new Date(day);
    const mins = Math.floor(relY / HOUR_HEIGHT * 4) * 15;
    slot.setHours(Math.floor(mins / 60), mins % 60, 0, 0);
    return slot;
  };

  return (
    <div className="cal-week">
      <div className="cal-week-header" style={{ gridTemplateColumns: cols }}>
        <div className="cal-week-header-gutter cal-wk-num-header">Wk {isoWeek(days[0])}</div>
        {days.map((day, i) => (
          <div key={i} className={`cal-week-header-day${sameDay(day, today) ? ' today' : ''}`}
            onClick={() => days.length > 1 && onShowDay(day)} style={days.length > 1 ? { cursor: 'pointer' } : undefined}>
            <span className="dow">{DOW[(day.getDay() + 6) % 7]}</span>
            <span className="dom">{day.getDate()}</span>
          </div>
        ))}
      </div>
      <div className="cal-allday-row" style={{ gridTemplateColumns: cols }}>
        <div className="cal-allday-gutter">all-day</div>
        {days.map((day, i) => (
          <div key={i} className="cal-allday-cell">
            {allDay.filter(ev => onDay(ev, day)).map(ev => (
              <div key={ev.key} className="cal-allday-event" style={{ background: ev.displayColor }} title={ev.label}
                onClick={e => { e.stopPropagation(); onEventClick(ev); }}>{ev.label}</div>
            ))}
          </div>
        ))}
      </div>
      <div className="cal-week-body" ref={bodyRef} style={{ gridTemplateColumns: cols }}>
        <div className="cal-week-time-col">
          {Array.from({ length: 24 }, (_, h) => <div key={h} className="cal-hour-label">{h === 0 ? '' : `${String(h).padStart(2, '0')}:00`}</div>)}
        </div>
        {days.map((day, di) => {
          const dayStart = startOfDay(day), dayEnd = endOfDay(day);
          const dayEvs = timed.filter(ev => onDay(ev, day));
          const layout = layoutColumns(dayEvs);
          return (
            <div key={di} className="cal-week-day-col" onClick={e => onSlotClick(slotFromClick(day, e))}>
              {Array.from({ length: 24 }, (_, h) => <div key={h} className="cal-hour-line" />)}
              {sameDay(day, today) && <div className="cal-now-line" style={{ top: nowTop }} />}
              {dayEvs.map(ev => {
                // Events running past midnight are cut at the day's edges
                const s = new Date(Math.max(+new Date(ev.start), +dayStart));
                const e = new Date(Math.min(+new Date(ev.end), +dayEnd));
                const { col, cols: n } = layout.get(ev);
                return (
                  <div key={ev.key} className="cal-week-event" title={ev.label}
                    style={{
                      top: (s.getHours() + s.getMinutes() / 60) * HOUR_HEIGHT,
                      height: Math.max((e - s) / 3600000 * HOUR_HEIGHT, 20),
                      left: `calc(${col / n * 100}% + 2px)`,
                      width: `calc(${100 / n}% - 4px)`,
                      background: ev.displayColor,
                    }}
                    onClick={e2 => { e2.stopPropagation(); onEventClick(ev); }}>
                    <strong>{ev.label}</strong>
                    <div>{fmtTime(new Date(ev.start))}–{fmtTime(new Date(ev.end))}</div>
                    {ev.location && <div className="cal-event-loc">{ev.location}</div>}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── "Calendars" popover in My Calendar ──────────────────────────────────────
function CalendarPicker({ prefs, projects, onChange, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    const away = e => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const esc  = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('pointerdown', away); document.removeEventListener('keydown', esc); };
  }, [onClose]);

  const selected = new Map((prefs.projects || []).map(p => [String(p.project), p.color]));

  const toggleProject = (p) => {
    if (selected.has(p._id)) {
      onChange({ projects: prefs.projects.filter(x => String(x.project) !== p._id) });
    } else {
      const used = new Set([...selected.values(), PERSONAL_COLOR]);
      const color = COLORS.find(c => !used.has(c)) || COLORS[selected.size % COLORS.length];
      onChange({ projects: [...(prefs.projects || []), { project: p._id, color }] });
    }
  };

  const Row = ({ checked, color, label, onToggle }) => (
    <label className="cal-pick-row">
      <input type="checkbox" checked={checked} onChange={onToggle} />
      <span className="cal-pick-dot" style={{ background: color }} />
      <span className="cal-pick-label">{label}</span>
    </label>
  );

  return (
    <div className="cal-picker" ref={ref}>
      <div className="cal-pick-section">My calendar</div>
      <Row checked={prefs.personal !== false} color={PERSONAL_COLOR} label="My appointments" onToggle={() => onChange({ personal: prefs.personal === false })} />
      <Row checked={prefs.holidays !== false} color={HOLIDAY_COLOR} label="Danish holidays" onToggle={() => onChange({ holidays: prefs.holidays === false })} />
      <Row checked={prefs.birthdays !== false} color={BIRTHDAY_COLOR} label="Birthdays" onToggle={() => onChange({ birthdays: prefs.birthdays === false })} />
      <div className="cal-pick-section">Project calendars</div>
      {projects.length === 0 && <div className="cal-pick-empty">No projects.</div>}
      {projects.map(p => (
        <Row key={p._id} checked={selected.has(p._id)} color={selected.get(p._id) || 'transparent'}
          label={projectLabel(p)} onToggle={() => toggleProject(p)} />
      ))}
    </div>
  );
}

// ── Root ─────────────────────────────────────────────────────────────────────
// mode 'project' — one project's calendar (everyone with the project sees it, foreman+ edits)
// mode 'my'      — foreman+: own appointments + chosen project calendars, holidays, birthdays
export default function Calendar({ mode, project, projects = [], role, socket }) {
  const isMy    = mode === 'my';
  const canEdit = WRITE_ROLES.includes(role);

  const [view, setView] = useState(() => { try { return localStorage.getItem(VIEW_KEY) || 'month'; } catch { return 'month'; } });
  const [cursor, setCursor]   = useState(() => new Date());
  const [events, setEvents]   = useState([]);
  const [prefs, setPrefs]     = useState(null);
  const [people, setPeople]   = useState([]);
  const [form, setForm]       = useState(null);
  const [picking, setPicking] = useState(false);
  const [error, setError]     = useState('');

  const { start, end } = useMemo(() => visibleRange(view, cursor), [view, cursor]);
  const prefProjectKey = (prefs?.projects || []).map(p => String(p.project)).sort().join(',');

  useEffect(() => { try { localStorage.setItem(VIEW_KEY, view); } catch {} }, [view]);

  useEffect(() => {
    if (!isMy) return;
    getCalendarPrefs().then(r => setPrefs(r.data)).catch(() => setPrefs({}));
    getBirthdays().then(r => setPeople(r.data)).catch(() => {});
  }, [isMy]);

  const load = useCallback(async () => {
    try {
      if (isMy) {
        if (!prefs) return;
        setEvents((await getMyEvents(start, end)).data);
      } else if (project?._id) {
        setEvents((await getProjectEvents(project._id, start, end)).data);
      }
      setError('');
    } catch {
      setError('Could not load the calendar.');
    }
  }, [isMy, project?._id, start, end, prefProjectKey, !!prefs]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // Someone else added/changed an event
  const loadRef = useRef(load);
  loadRef.current = load;
  const prefsRef = useRef(prefs);
  prefsRef.current = prefs;
  useEffect(() => {
    if (!socket) return;
    const onChanged = ({ project: pid }) => {
      if (isMy) {
        if (!pid || (prefsRef.current?.projects || []).some(p => String(p.project) === pid)) loadRef.current();
      } else if (pid === project?._id) loadRef.current();
    };
    socket.on('calendar:changed', onChanged);
    return () => socket.off('calendar:changed', onChanged);
  }, [socket, isMy, project?._id]);

  // Calendars the events belong to: id → { name, color }
  const calendars = useMemo(() => {
    if (!isMy) return [{ id: project?._id, name: projectLabel(project), color: COLORS[0] }];
    const list = [{ id: 'personal', name: 'My appointments', color: PERSONAL_COLOR }];
    for (const p of prefs?.projects || []) {
      const proj = projects.find(x => x._id === String(p.project));
      if (proj) list.push({ id: proj._id, name: projectLabel(proj), color: p.color || COLORS[0] });
    }
    return list;
  }, [isMy, project, projects, prefs]);

  const shown = useMemo(() => {
    const calMap = Object.fromEntries(calendars.map(c => [c.id, c]));
    const list = [];
    for (const ev of events) {
      const calId = ev.project || 'personal';
      if (isMy && calId === 'personal' && prefs?.personal === false) continue;
      const cal = calMap[calId];
      if (!cal) continue;
      // In My Calendar, project events start with the project ("#484 Vigilant Building meeting")
      const label = isMy && ev.project ? `${cal.name} ${ev.title}` : ev.title;
      list.push({ ...ev, key: `${ev._id}-${ev.start}`, label, displayColor: ev.color || cal.color, calendarName: cal.name });
    }
    if (isMy && prefs) {
      const extra = [];
      if (prefs.holidays !== false) extra.push(...generatedInRange(start, end, y => danishHolidays(y, HOLIDAY_COLOR)));
      if (prefs.birthdays !== false) extra.push(...generatedInRange(start, end, y => birthdays(people, y, BIRTHDAY_COLOR)));
      for (const ev of extra) {
        list.push({ ...ev, key: ev._id, label: ev.title, displayColor: ev.color, calendarName: ev.calendar === 'holidays' ? 'Danish holidays' : 'Birthdays' });
      }
    }
    return list;
  }, [events, calendars, isMy, prefs, people, start, end]);

  const changePrefs = async (patch) => {
    const next = { ...prefs, ...patch };
    setPrefs(next);
    try { setPrefs((await saveCalendarPrefs(patch)).data); } catch { setError('Could not save your calendar choices.'); }
  };

  const navigate = (dir) => setCursor(prev => {
    const d = new Date(prev);
    if (view === 'month')     d.setMonth(d.getMonth() + dir, 1);
    else if (view === 'week') d.setDate(d.getDate() + dir * 7);
    else                      d.setDate(d.getDate() + dir);
    return d;
  });

  const title = (() => {
    if (view === 'month') return `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`;
    if (view === 'week') {
      const ws = startOfWeek(cursor), we = addDays(ws, 6);
      return ws.getMonth() === we.getMonth()
        ? `${ws.getDate()}–${we.getDate()} ${MONTHS[ws.getMonth()]} ${ws.getFullYear()}`
        : `${ws.getDate()} ${MONTHS[ws.getMonth()].slice(0, 3)} – ${we.getDate()} ${MONTHS[we.getMonth()].slice(0, 3)} ${we.getFullYear()}`;
    }
    return `${DAY_NAMES[cursor.getDay()]} ${cursor.getDate()} ${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`;
  })();

  const showDay = (day) => { setCursor(new Date(day)); setView('day'); };

  const newAt = (date) => { if (canEdit) setForm({ defaultStart: date }); };
  const newOnDay = (day) => { const d = new Date(day); d.setHours(9, 0, 0, 0); newAt(d); };
  // Month view: next whole hour from now; week/day: 09:00 on the shown day
  const newFromToolbar = () => {
    const d = view === 'month' ? new Date() : new Date(cursor);
    if (view === 'month') d.setHours(d.getHours() + 1, 0, 0, 0);
    else d.setHours(9, 0, 0, 0);
    newAt(d);
  };
  const openEvent = (ev) => setForm({ event: ev, readOnly: ev.readOnly || !canEdit });

  const handleSave = async (payload, existing) => {
    try {
      if (existing?._id) {
        const { project: _ignored, ...changes } = payload;
        await updateEvent(existing._id, changes);
      } else {
        await createEvent(payload);
      }
      setForm(null);
      load();
    } catch (e) {
      setError(e.response?.data?.msg || 'Could not save the event.');
    }
  };

  const handleDelete = async () => {
    const ev = form?.event;
    if (!ev?._id) return;
    if (!window.confirm(ev.recurrence ? `Delete "${ev.title}" and all its repeats?` : `Delete "${ev.title}"?`)) return;
    try {
      await deleteEvent(ev._id);
      setForm(null);
      load();
    } catch {
      setError('Could not delete the event.');
    }
  };

  const days = view === 'week'
    ? Array.from({ length: 7 }, (_, i) => addDays(startOfWeek(cursor), i))
    : [startOfDay(cursor)];

  return (
    <div className="cal-root">
      <div className="cal-toolbar">
        <div className="cal-view-group">
          {['month', 'week', 'day'].map(v => (
            <button key={v} className={`cal-btn${view === v ? ' active' : ''}`} onClick={() => setView(v)}>
              {v.charAt(0).toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>
        <button className="cal-btn" onClick={() => setCursor(new Date())}>Today</button>
        <div className="cal-nav-group">
          <button className="cal-btn-icon" title="Previous" onClick={() => navigate(-1)}><VscChevronLeft size={16} /></button>
          <span className="cal-toolbar-title">{title}</span>
          <button className="cal-btn-icon" title="Next" onClick={() => navigate(1)}><VscChevronRight size={16} /></button>
        </div>
        <div className="cal-toolbar-spacer" />
        {isMy && (
          <div className="cal-picker-wrap">
            <button className={`cal-btn${picking ? ' active' : ''}`} onClick={() => setPicking(p => !p)} disabled={!prefs}>
              <VscLayers size={13} /> Calendars
            </button>
            {picking && prefs && (
              <CalendarPicker prefs={prefs} projects={projects} onChange={changePrefs} onClose={() => setPicking(false)} />
            )}
          </div>
        )}
        {canEdit && (
          <button className="btn-primary cal-new-btn" onClick={newFromToolbar}>
            <VscAdd size={13} /> New event
          </button>
        )}
      </div>

      {error && <div className="cal-error">{error}</div>}

      {view === 'month'
        ? <MonthView cursor={cursor} events={shown} onDayClick={newOnDay} onEventClick={openEvent} onShowDay={showDay} />
        : <TimeGrid key={`${view}-${+days[0]}`} days={days} events={shown} onSlotClick={newAt} onEventClick={openEvent} onShowDay={showDay} />}

      {form && (
        <EventForm
          event={form.event}
          defaultStart={form.defaultStart}
          readOnly={form.readOnly}
          calendars={calendars}
          defaultCalendar={isMy ? 'personal' : project?._id}
          onSave={handleSave}
          onDelete={form.event && !form.event.readOnly ? handleDelete : null}
          onClose={() => setForm(null)}
        />
      )}
    </div>
  );
}
